/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  LinearProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import useFacility from '../../hooks/useFacility';

/**
 * ChambersReport - New Schema Version
 * 
 * Uses the new schema structure:
 * - chamber.capacity: { unit: ObjectId, max_capacity: Number }
 * - chamber.available: calculated available capacity
 * - chamber.total_used: calculated total used capacity
 * - chamber.usage_by_rate: breakdown by rate_id
 * - chamber.active_assignments: current active assignments
 */
// A project can hold several active assignments in one compartment (e.g. at
// different rates or start dates). The report shows one entry per project with
// its total space in that compartment.
const groupAssignmentsByProject = (assignments = []) => {
  const byProject = new Map();
  for (const a of assignments) {
    const key = a.project_id || a.project_title || a._id;
    if (!byProject.has(key)) {
      byProject.set(key, {
        key,
        project_id: a.project_id,
        project_title: a.project_title,
        quantity: 0,
        assignments: []
      });
    }
    const entry = byProject.get(key);
    entry.quantity += Number(a.quantity) || 0;
    entry.assignments.push(a);
  }
  return [...byProject.values()].map(e => ({ ...e, quantity: Math.round(e.quantity * 100) / 100 }));
};

const ChambersReport = () => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  const [chambers, setChambers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [filteredChambers, setFilteredChambers] = useState([]);
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  // Pagination, search, date filter, sort states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedAssignments, setExpandedAssignments] = useState(new Set());

  // Print / Export dialog state
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState('current'); // 'current' | 'period'
  const [exportFormat, setExportFormat] = useState('pdf'); // 'pdf' | 'csv'
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [allChambers, setAllChambers] = useState([]);
  const [loadingAllChambers, setLoadingAllChambers] = useState(false);
  const [selectedExportChambers, setSelectedExportChambers] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const toggleAssignmentsExpanded = (chamberId) => {
    setExpandedAssignments(prev => {
      const next = new Set(prev);
      if (next.has(chamberId)) {
        next.delete(chamberId);
      } else {
        next.add(chamberId);
      }
      return next;
    });
  };

  // ── Print / Export ────────────────────────────────────────────────
  // Load every compartment (across all facilities, including inactive) so
  // admins can pick any combination for the report.
  const openExportDialog = async () => {
    setExportError(null);
    setExportOpen(true);
    if (allChambers.length === 0) {
      try {
        setLoadingAllChambers(true);
        const res = await axios.get('/api/chambers?include_inactive=true');
        setAllChambers(res.data);
      } catch (err) {
        console.error('Error loading compartments for export:', err);
        setExportError('Failed to load compartments.');
      } finally {
        setLoadingAllChambers(false);
      }
    }
  };

  const chambersByFacility = useMemo(() => {
    const map = new Map();
    for (const c of allChambers) {
      const fid = (c.facility?._id || c.facility || 'unassigned').toString();
      const fname = c.facility?.facility_name || 'Unassigned Facility';
      if (!map.has(fid)) map.set(fid, { id: fid, name: fname, chambers: [] });
      map.get(fid).chambers.push(c);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allChambers]);

  const toggleExportChamber = (chamberId) => {
    setSelectedExportChambers(prev => {
      const next = new Set(prev);
      if (next.has(chamberId)) next.delete(chamberId);
      else next.add(chamberId);
      return next;
    });
  };

  const toggleExportFacility = (facilityGroup) => {
    const ids = facilityGroup.chambers.map(c => c._id);
    const allSelected = ids.every(id => selectedExportChambers.has(id));
    setSelectedExportChambers(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const allExportSelected = allChambers.length > 0 &&
    allChambers.every(c => selectedExportChambers.has(c._id));

  const toggleSelectAllExport = () => {
    setSelectedExportChambers(prev => {
      if (allExportSelected) return new Set();
      return new Set(allChambers.map(c => c._id));
    });
  };

  const handleExportDownload = async () => {
    setExportError(null);

    if (exportMode === 'period') {
      if (!exportStart || !exportEnd) {
        setExportError('Please select both a start and end date for the time period.');
        return;
      }
      if (exportStart > exportEnd) {
        setExportError('Start date must be on or before the end date.');
        return;
      }
    }

    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      params.append('mode', exportMode);
      if (exportMode === 'period') {
        params.append('start_date', exportStart);
        params.append('end_date', exportEnd);
      }
      const ids = Array.from(selectedExportChambers);
      // No explicit selection -> include all compartments.
      if (ids.length > 0) params.append('chambers', ids.join(','));

      const res = await axios.get(
        `/api/admin/reports/compartments-export?${params.toString()}`,
        { responseType: 'blob' }
      );

      const mime = exportFormat === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8;';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = exportFormat === 'pdf' ? 'pdf' : 'csv';
      link.setAttribute('download', `compartment-project-report-${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (err) {
      console.error('Error exporting compartment project report:', err);
      setExportError('Failed to generate the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const facilityParam = selectedFacility !== 'all' ? `?facility=${selectedFacility}` : '';
      
      const [chambersRes, facilitiesRes] = await Promise.all([
        axios.get(`/api/chambers${facilityParam}`),
        axios.get('/api/facilities')
      ]);
      
      setChambers(chambersRes.data);
      setFilteredChambers(chambersRes.data);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate utilization percentage
  const getUtilization = (chamber) => {
    const max = chamber.max_capacity || chamber.capacity?.max_capacity || 0;
    const used = chamber.total_used || 0;
    if (max === 0) return 0;
    return Math.round((used / max) * 100);
  };

  // Get utilization color
  const getUtilizationColor = (percent) => {
    if (percent >= 90) return 'error';
    if (percent >= 70) return 'warning';
    return 'success';
  };

  // Extract a comparable value for a given column from a chamber row
  const getSortValue = (chamber, column) => {
    switch (column) {
      case 'chamber':
        return (chamber.chamber || '').toString().toLowerCase();
      case 'facility':
        return (chamber.facility?.facility_name || '').toLowerCase();
      case 'unit':
        return (chamber.capacity?.unit?.base_unit || '').toLowerCase();
      case 'capacity':
        return chamber.max_capacity || chamber.capacity?.max_capacity || 0;
      case 'utilization':
        return getUtilization(chamber);
      case 'active_assignments':
        return groupAssignmentsByProject(chamber.active_assignments).length;
      default:
        return '';
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const resetSort = () => {
    setSortColumn(null);
    setSortDirection('asc');
  };

  useEffect(() => {
    let filtered = chambers;

    // Enforce the selected facility client-side too. The list is also fetched
    // filtered by facility, but this guards against any rows from another
    // facility lingering in state (e.g. the initial "all" fetch before the
    // facility preference loads), so sorting can never surface them.
    if (selectedFacility && selectedFacility !== 'all') {
      filtered = filtered.filter(chamber => {
        const facilityId = (chamber.facility?._id || chamber.facility || '').toString();
        return facilityId === selectedFacility.toString();
      });
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(chamber => 
        chamber.chamber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chamber.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chamber.facility?.facility_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chamber.capacity?.unit?.base_unit?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sort if a column is selected. Otherwise leave the API order intact.
    if (sortColumn) {
      const dir = sortDirection === 'asc' ? 1 : -1;
      filtered = [...filtered].sort((a, b) => {
        const av = getSortValue(a, sortColumn);
        const bv = getSortValue(b, sortColumn);
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    setFilteredChambers(filtered);
    setPage(0);
  }, [searchQuery, chambers, sortColumn, sortDirection, selectedFacility]);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading compartments...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search compartments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          size="small"
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        {(startDate || endDate) && (
          <Tooltip title="Clear date filter">
            <IconButton onClick={() => { setStartDate(''); setEndDate(''); }} size="small">
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={resetSort}
          disabled={!sortColumn}
        >
          Reset Sort
        </Button>

        <Button
          variant="contained"
          size="small"
          startIcon={<PrintIcon />}
          onClick={openExportDialog}
        >
          Print / Export
        </Button>
      </Box>

      {/* Chambers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {[
                { id: 'chamber', label: 'Compartment' },
                { id: 'facility', label: 'Facility' },
                { id: 'unit', label: 'Unit' },
                { id: 'capacity', label: 'Capacity' },
                { id: 'utilization', label: 'Utilization' },
                { id: 'active_assignments', label: 'Active Assignments' },
              ].map(col => (
                <TableCell key={col.id} sx={{ fontWeight: 'bold' }} sortDirection={sortColumn === col.id ? sortDirection : false}>
                  <TableSortLabel
                    active={sortColumn === col.id}
                    direction={sortColumn === col.id ? sortDirection : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChambers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">No compartments found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredChambers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(chamber => {
                  const utilization = getUtilization(chamber);
                  const maxCapacity = chamber.max_capacity || chamber.capacity?.max_capacity || 0;
                  const available = Math.max(0, chamber.available ?? maxCapacity);
                  const used = chamber.total_used || 0;
                  const unit = chamber.capacity?.unit;
                  const activeAssignments = groupAssignmentsByProject(chamber.active_assignments);
                  const unitLabel = unit?.unit_short || unit?.base_unit || 'units';

                  return (
                    <TableRow key={chamber._id} hover>
                      {/* Chamber Name & Description */}
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {chamber.chamber}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {chamber.description}
                        </Typography>
                      </TableCell>

                      {/* Facility */}
                      <TableCell>
                        {chamber.facility?.facility_name || '-'}
                      </TableCell>

                      {/* Unit */}
                      <TableCell>
                        {unit ? (
                          <Chip 
                            label={unit.base_unit} 
                            size="small" 
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            No unit assigned
                          </Typography>
                        )}
                      </TableCell>

                      {/* Capacity */}
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            <strong>{available}</strong> / {maxCapacity} available
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {used} in use
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Utilization */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={utilization} 
                            color={getUtilizationColor(utilization)}
                            sx={{ width: 60, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="body2">
                            {utilization}%
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Active Assignments */}
                      <TableCell>
                        {activeAssignments.length > 0 ? (
                          (() => {
                            const isExpanded = expandedAssignments.has(chamber._id);
                            const visibleAssignments = isExpanded
                              ? activeAssignments
                              : activeAssignments.slice(0, 3);
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {visibleAssignments.map((entry) => (
                                  <Box 
                                    key={entry.key} 
                                    sx={{ 
                                      p: 1, 
                                      bgcolor: '#e3f2fd', 
                                      borderRadius: 1,
                                      borderLeft: '3px solid #1976d2'
                                    }}
                                  >
                                    <Typography variant="caption" fontWeight="bold" display="block">
                                      {entry.project_title || entry.project_id || 'Project'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {entry.quantity} {unitLabel} in use
                                    </Typography>
                                  </Box>
                                ))}
                                {activeAssignments.length > 3 && (
                                  <Typography
                                    variant="caption"
                                    color="primary"
                                    onClick={() => toggleAssignmentsExpanded(chamber._id)}
                                    sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                                  >
                                    {isExpanded ? 'Show less' : `+${activeAssignments.length - 3} more`}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            No active assignments
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <FormControl size="small">
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            variant="standard"
          >
            <MenuItem value={10}>Show 10 entries</MenuItem>
            <MenuItem value={25}>Show 25 entries</MenuItem>
            <MenuItem value={50}>Show 50 entries</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label="First page"
            onClick={() => handleChangePage(0)}
            disabled={page === 0}
            size="small"
          >
            <FirstPage />
          </IconButton>
          <IconButton aria-label="Previous page"
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
            size="small"
          >
            <KeyboardArrowLeft />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 2 }}>
            Page {page + 1} of {Math.max(1, Math.ceil(filteredChambers.length / rowsPerPage))}
          </Typography>
          <IconButton aria-label="Next page"
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= Math.ceil(filteredChambers.length / rowsPerPage) - 1}
            size="small"
          >
            <KeyboardArrowRight />
          </IconButton>
          <IconButton aria-label="Last page"
            onClick={() => handleChangePage(Math.ceil(filteredChambers.length / rowsPerPage) - 1)}
            disabled={page >= Math.ceil(filteredChambers.length / rowsPerPage) - 1}
            size="small"
          >
            <LastPage />
          </IconButton>
        </Box>

        <Typography variant="body2" color="textSecondary">
          Total: {filteredChambers.length} compartments
        </Typography>
      </Box>

      {/* Print / Export dialog */}
      <Dialog open={exportOpen} onClose={() => !exporting && setExportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Print / Export Compartment Project Report</DialogTitle>
        <DialogContent dividers>
          {exportError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}

          {/* Report type */}
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Report type</FormLabel>
            <RadioGroup
              value={exportMode}
              onChange={(e) => setExportMode(e.target.value)}
            >
              <FormControlLabel
                value="current"
                control={<Radio />}
                label="Current assignments — active projects in each compartment right now"
              />
              <FormControlLabel
                value="period"
                control={<Radio />}
                label="Time period — all projects assigned to the compartments during a date range"
              />
            </RadioGroup>
          </FormControl>

          {exportMode === 'period' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={exportStart}
                onChange={(e) => setExportStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={exportEnd}
                onChange={(e) => setExportEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Compartment selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormLabel>Compartments to include</FormLabel>
            {allChambers.length > 0 && (
              <FormControlLabel
                control={<Checkbox checked={allExportSelected} onChange={toggleSelectAllExport} size="small" />}
                label="Select all"
              />
            )}
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            Pick individual compartments, or use a facility checkbox to include all of its compartments. Leaving everything unchecked includes every compartment.
          </Typography>

          {loadingAllChambers ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading compartments…</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              {chambersByFacility.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                  No compartments available.
                </Typography>
              ) : (
                chambersByFacility.map(group => {
                  const groupIds = group.chambers.map(c => c._id);
                  const allChecked = groupIds.every(id => selectedExportChambers.has(id));
                  const someChecked = !allChecked && groupIds.some(id => selectedExportChambers.has(id));
                  return (
                    <Box key={group.id} sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={() => toggleExportFacility(group)}
                            size="small"
                          />
                        }
                        label={<Typography fontWeight="bold">{group.name}</Typography>}
                      />
                      <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column' }}>
                        {group.chambers.map(c => (
                          <FormControlLabel
                            key={c._id}
                            control={
                              <Checkbox
                                checked={selectedExportChambers.has(c._id)}
                                onChange={() => toggleExportChamber(c._id)}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {c.chamber}
                                {c.description ? ` — ${c.description}` : ''}
                                {c.status && c.status !== 'Active' ? ` (${c.status})` : ''}
                              </Typography>
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Format */}
          <FormControl>
            <FormLabel>File format</FormLabel>
            <RadioGroup
              row
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportDownload}
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
          >
            {exporting ? 'Generating…' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChambersReport;
